// src/components/LengthAdjustmentChips.tsx
//
// Two small chips at the bottom of every assistant message that
// let the user re-roll the same answer at a different length:
// '−' for tighter, '+' for fuller. Routes through the existing
// onFollowUpClick channel with a special prefix the chat AI
// interprets as a length-targeting instruction (no parser changes
// elsewhere needed — it's just a slightly weird user message).
//
// Why chips and not buttons: chips are visually quiet, fit the
// existing follow-up question chip style, and degrade to nothing
// awkward when the user ignores them.

import React from 'react';
import { Box, Chip, useTheme } from '@mui/material';
import { useT } from '../i18n/useT';
import { Remove, Add } from '@mui/icons-material';

interface Props {
  /** The previous user prompt — what we want to re-ask, but with
   *  a different target length. When unavailable we degrade
   *  silently (the chips don't render). */
  previousUserPrompt: string;
  /** The previous assistant content, used to give the AI context
   *  about what to expand/contract. Truncated to ~400 chars. */
  previousAssistantText: string;
  onFollowUpClick: (prompt: string) => void;
}

const TRUNCATE = 400;

export const LengthAdjustmentChips: React.FC<Props> = ({
  previousUserPrompt,
  previousAssistantText,
  onFollowUpClick,
}) => {
  const theme = useTheme();
  const t = useT();
  if (!previousUserPrompt || !previousAssistantText) return null;

  // The prompts are deliberately written like a natural user ask
  // so the AI doesn't get confused into thinking these are system
  // directives. They are short, framed in first-person, and end
  // with what the user actually wants.
  const shorterPrompt =
    `That was a lot — give me the same idea in about half the length, just the most important point or two. Original question was: "${previousUserPrompt.slice(0, 160)}".`;
  const longerPrompt =
    `Go deeper on what you just said. Same topic, more detail — names, dates, the historical context, any specifics you skipped. Original question was: "${previousUserPrompt.slice(0, 160)}". Don't repeat the surface stuff you already covered; assume I read it.`;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.75,
        mt: 0.5,
        // Tucked to the right so they don't crowd the prose's
        // natural left-aligned read.
        justifyContent: 'flex-end',
        alignItems: 'center',
      }}
    >
      <Chip
        size="small"
        icon={<Remove sx={{ fontSize: 14 }} />}
        label={t("shorter")}
        clickable
        onClick={() => onFollowUpClick(shorterPrompt)}
        sx={{
          fontSize: '0.72rem',
          height: 22,
          color: 'text.secondary',
          borderColor: theme.palette.divider,
          bgcolor: 'transparent',
          '& .MuiChip-icon': { color: 'text.secondary', ml: 0.5 },
          '&:hover': { bgcolor: 'action.hover' },
        }}
        variant="outlined"
      />
      <Chip
        size="small"
        icon={<Add sx={{ fontSize: 14 }} />}
        label={t("more depth")}
        clickable
        onClick={() => onFollowUpClick(longerPrompt)}
        sx={{
          fontSize: '0.72rem',
          height: 22,
          color: 'text.secondary',
          borderColor: theme.palette.divider,
          bgcolor: 'transparent',
          '& .MuiChip-icon': { color: 'text.secondary', ml: 0.5 },
          '&:hover': { bgcolor: 'action.hover' },
        }}
        variant="outlined"
      />
    </Box>
  );
};

// Avoid unused-import noise when callers do tree-shaking
export const _LENGTH_ADJ_TRUNCATE = TRUNCATE;
